import { useState, useEffect } from "react";
import {BrowserRouter as Router, Routes, Route, useNavigate} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AOS from "aos";
import "aos/dist/aos.css";

import { useAuth } from "./contexts/AuthContext";

import Home from "./pages/Home";
import Properties from "./pages/Properties";
import Dining from "./pages/Dining";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import People from "./pages/People";
import ThankYou from "./pages/ThankYou";
import Accommodation from "./pages/Accommodation";
import AccommodationDetail from "./pages/AccommodationDetail";
import ElgroInnHotel from "./pages/ElgroInnHotel";
import BaltimoreMansions from "./pages/BaltimoreMansions";
import OranjevilleSelfCatering from "./pages/OranjevilleSelfCatering";
import PasteurHouse from "./pages/PasteurHouse";
import SecundaBaltimoreSuites from "./pages/SecundaBaltimoreSuites";
import DelRio from "./pages/DelRio";
import Karlandi from "./pages/Karlandi";
import BookingThankYou from "./pages/BookingThankYou";
import BookingError from "./pages/BookingError";
import AdminDashboard from "./pages/AdminDashboard";
import Profile from "./pages/Profile";
import AcknowledgedMessagesHistory from "./pages/AcknowledgedMessagesHistory";
import BookingDetails from "./pages/BookingDetails";
import BookingConfirmation from "./pages/BookingConfirmation";
import BookingsTable from "./pages/BookingsTable";
import Login from "./pages/Login";

import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import Footer from "./components/Footer";
import LoginModal from "./components/LoginModal";
import MessageModal from "./components/MessageModal";
import LogoutButton from "./components/LogoutButton";


function AppContent() {
    const { user, isLoggedIn, login, logout, messages, hasUnacknowledgedMessages, setHasUnacknowledgedMessages } = useAuth();

    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Automatically open message modal after login if there are unacknowledged messages
    useEffect(() => {
        if (isLoggedIn && messages?.length > 0 && hasUnacknowledgedMessages) {
            setIsMessageModalOpen(true);
        }
    }, [isLoggedIn, messages, hasUnacknowledgedMessages]);

    const openLogin = () => setIsLoginOpen(true);
    const closeLogin = () => setIsLoginOpen(false);

    const handleLoginSuccess = async (userData) => {
        await login(userData);
        setIsLoginOpen(false);
    };

    return (
        <Router>
            <ScrollToTop />
            <Helmet>
                <title>Benecke Group | Accommodation, Dining & Property Rentals in South Africa</title>
                <meta name="description" content="Family-owned South African company offering accommodation, dining, and rental properties across the Vaal, Vanderbijlpark, and Secunda." />
                <meta name="keywords" content="Benecke Group, ReservationForm Vaal, Contractor Housing, Rental Properties Gauteng, Spur Breakfast, Hotels in Vanderbijlpark, Self-catering units, Property rentals South Africa" />
                <meta property="og:title" content="Benecke Group | Stay, Rent, Dine" />
                <meta property="og:description" content="Affordable accommodation, property rentals, and dining options across South Africa." />
                <meta property="og:image" content="https://beneckegroup.com/images/social-thumbnail.jpg" />
                <meta property="og:url" content="https://beneckegroup.com" />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="canonical" href="https://beneckegroup.com" />
                <link rel="icon" href="/favicon.ico" />
                <link rel="shortcut icon" href="/favicon.png" type="image/png" />

                {/* Structured Data */}
                <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LodgingBusiness",
            "name": "Baltimore Suites by Benecke Group",
            "description": "Affordable accommodation in Vanderbijlpark with Spur breakfast included.",
            "image": "https://beneckegroup.com/images/baltimore.jpg",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1 Baltimore Street",
              "addressLocality": "Vanderbijlpark",
              "addressRegion": "Gauteng",
              "postalCode": "1911",
              "addressCountry": "ZA"
            },
            "telephone": "+27-11-123-4567",
            "url": "https://beneckegroup.com"
          }
        `}</script>

                <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Benecke Group Rentals",
            "description": "Affordable residential and commercial properties across the Vaal Triangle.",
            "url": "https://beneckegroup.com",
            "areaServed": "Gauteng, South Africa",
            "telephone": "+27-11-123-4567"
          }
        `}</script>

                <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Restaurant",
            "name": "Spur at Benecke Group",
            "servesCuisine": ["Steakhouse", "Breakfast", "Family Dining"],
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Benecke Complex, Vaal Mall",
              "addressLocality": "Vanderbijlpark",
              "addressRegion": "Gauteng",
              "postalCode": "1911",
              "addressCountry": "ZA"
            },
            "telephone": "+27-11-456-7890",
            "url": "https://beneckegroup.com"
          }
        `}</script>
            </Helmet>

            <div className="flex flex-col min-h-screen">
                <Navbar onLoginClick={openLogin} />

                <LoginModal
                    isOpen={isLoginOpen}
                    onClose={closeLogin}
                    onLoginSuccess={handleLoginSuccess}
                />


                    <MessageModal
                        isOpen={isMessageModalOpen}
                        onClose={() => {
                            setIsMessageModalOpen(false);
                            setHasUnacknowledgedMessages(false);
                        }}
                    />



                <main className="flex-grow">
                    {isLoggedIn && hasUnacknowledgedMessages && (
                        <div className="text-center py-3 font-semibold">
                            🔒 Please acknowledge all messages to continue.
                        </div>
                    )}

                    {isLoggedIn && !hasUnacknowledgedMessages && (
                        <div className="bg-[#F29E38] text-[#023059] text-center py-3 font-semibold">
                            <p> Welcome back, {user?.name || "Guest"} </p>

                            <LogoutButton />

                        </div>
                    )}

                    {(!isLoggedIn || !hasUnacknowledgedMessages) && (
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/properties" element={<Properties />} />
                            <Route path="/dining" element={<Dining />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/careers" element={<Careers />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/people" element={<People />} />
                            <Route path="/thank-you" element={<ThankYou />} />
                            <Route path="/accommodation" element={<Accommodation />} />
                            <Route path="/accommodation/:id" element={<AccommodationDetail />} />
                            <Route path="/elgro-inn-hotel" element={<ElgroInnHotel />} />
                            <Route path="/baltimore-mansions" element={<BaltimoreMansions />} />
                            <Route path="/oranjeville-self-catering" element={<OranjevilleSelfCatering />} />
                            <Route path="/pasteur-house" element={<PasteurHouse />} />
                            <Route path="/secunda-baltimore-suites" element={<SecundaBaltimoreSuites />} />
                            <Route path="/del-rio" element={<DelRio />} />
                            <Route path="/karlandi" element={<Karlandi />} />
                            <Route path="/booking/success" element={<BookingThankYou />} />
                            <Route path="/booking/error" element={<BookingError />} />
                            <Route path="/admin/dashboard" element={<AdminDashboard />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/messages/acknowledged" element={<AcknowledgedMessagesHistory />} />
                            <Route path="/admin/bookings/:id" element={<BookingDetails />} />
                            <Route path="/bookings/:token" element={<BookingConfirmation />} />
                            <Route path="/admin/bookings" element={<BookingsTable />} />
                            <Route path="/login" element={<Login />} />
                        </Routes>
                    )}
                </main>

                <Footer onLoginClick={openLogin} />
            </div>
        </Router>
    );
}

export default AppContent;
